import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Handshake, MessageCircle, FileText } from "lucide-react";
import PartnerProfileEditor from "@/components/admin/PartnerProfileEditor";
import { useLang } from "@/context/LangContext";
import { http, formatErr, waLink } from "@/lib/api";

const STATUSES = ["new", "contacted", "approved", "rejected"];
const CLS = { new: "bg-amber-100 text-amber-800", contacted: "bg-sky-100 text-sky-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-slate-100 text-slate-500" };
const intl = (p) => (p.startsWith("0") ? "62" + p.slice(1) : p);

function Docs({ a }) {
  const { t } = useLang();
  const open = async (d) => {
    const win = window.open("", "_blank");
    try {
      const { data } = await http.get(`/admin/partner-docs/${d.path}`, { responseType: "blob" });
      win.location.href = URL.createObjectURL(data);
    } catch (e) { win?.close(); toast.error(formatErr(e)); }
  };
  if (!a.documents?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <span className="text-xs font-semibold text-slate-500">{t("pt_docs_admin")}:</span>
      {a.documents.map((d, i) => (
        <button key={d.path} type="button" onClick={() => open(d)} data-testid={`partner-app-doc-${a.id}-${i}`} className="inline-flex max-w-[200px] items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-50"><FileText className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{d.name}</span></button>
      ))}
    </div>
  );
}

export default function PartnerApps() {
  const { t } = useLang();
  const [rows, setRows] = useState(null);
  useEffect(() => { http.get("/admin/partner-applications").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  const setStatus = async (a, status) => {
    try { const { data } = await http.patch(`/admin/partner-applications/${a.id}`, { status }); setRows(rows.map((x) => (x.id === a.id ? data : x))); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const msg = (a) => t("pt_wa_msg").replace("{name}", a.name).replace("{cat}", t(`pt_cat_${a.category}`));
  return (
    <div data-testid="admin-partner-apps" className="mt-10 rounded-3xl border border-slate-200 bg-white p-5">
      <h2 className="flex items-center gap-2 text-xl font-bold"><Handshake className="h-5 w-5 text-[#064E3B]" />{t("pt_admin_title")} {rows && <span className="text-sm font-semibold text-amber-700">· {t("pst_new")}: {rows.filter((a) => a.status === "new").length}</span>}</h2>
      {rows?.length === 0 && <p data-testid="partner-apps-empty" className="mt-3 text-sm text-slate-500">{t("pt_admin_empty")}</p>}
      <ul className="mt-4 space-y-3">{(rows || []).map((a) => (
        <li key={a.id} data-testid={`partner-app-${a.id}`} className="flex flex-wrap items-start gap-3 rounded-2xl border border-slate-200 p-4">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-900">{a.name} <span className="text-xs font-medium text-slate-500">· {t(`pt_cat_${a.category}`)} · {t(`city_${a.city}`)} · {a.created_at.slice(0, 10)} · {a.jobs || 0} {t("pt_jobs")}</span>
              {a.reports > 0 && <span data-testid={`partner-app-reports-${a.id}`} className="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">{a.reports} {t(a.reports === 1 ? "pt_report_one" : "pt_reports")}</span>}</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{a.details}</p>
            <Docs a={a} />
          </div>
          <div className="flex items-center gap-2">
            <a href={waLink(intl(a.phone), msg(a))} target="_blank" rel="noreferrer" data-testid={`partner-app-wa-${a.id}`} onClick={() => a.status === "new" && setStatus(a, "contacted")} className="inline-flex items-center gap-1 rounded-full bg-[#25D366] px-3 py-1.5 text-xs font-bold text-white transition-transform hover:-translate-y-0.5"><MessageCircle className="h-3.5 w-3.5" />{a.phone}</a>
            <select data-testid={`partner-app-status-${a.id}`} value={a.status} onChange={(e) => setStatus(a, e.target.value)} className={`rounded-full px-3 py-1.5 text-xs font-bold outline-none ${CLS[a.status]}`}>
              {STATUSES.map((s) => <option key={s} value={s}>{t(`pst_${s}`)}</option>)}
            </select>
          </div>
          <div className="w-full"><PartnerProfileEditor key={`${a.id}-${a.status}`} app={a} onSaved={(na) => setRows(rows.map((x) => (x.id === na.id ? na : x)))} /></div>
        </li>
      ))}</ul>
    </div>
  );
}
